import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';
import type {
  WishlistCreateBeneficiaryOption,
  WishlistCreateFieldErrors,
  WishlistCreatePersonDraft,
} from '@nexus/customer/wishlists/model';
import {
  UiActionCardComponent,
  UiButtonComponent,
  UiCardComponent,
  UiChoiceGroupComponent,
  UiInputComponent,
  UiSwitchRowComponent,
  type UiChoiceOption,
} from '@nexus/frontend-shared-ui';

@Component({
  selector: 'wishlist-create-beneficiary-selector',
  imports: [
    UiActionCardComponent,
    UiButtonComponent,
    UiCardComponent,
    UiChoiceGroupComponent,
    UiInputComponent,
    UiSwitchRowComponent,
  ],
  templateUrl: './wishlist-create-beneficiary-selector.component.html',
  styleUrl: './wishlist-create-beneficiary-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistCreateBeneficiarySelectorComponent {
  public readonly options: InputSignal<readonly WishlistCreateBeneficiaryOption[]> =
    input.required<readonly WishlistCreateBeneficiaryOption[]>();
  public readonly selectedOptionId: InputSignal<string | null> = input<string | null>(null);
  public readonly errors: InputSignal<WishlistCreateFieldErrors> = input.required<WishlistCreateFieldErrors>();
  public readonly addPersonOpen: InputSignal<boolean> = input<boolean>(false);
  public readonly personDraft: InputSignal<WishlistCreatePersonDraft> = input.required<WishlistCreatePersonDraft>();
  public readonly personCreatePending: InputSignal<boolean> = input<boolean>(false);
  public readonly personCreateDisabled: InputSignal<boolean> = input<boolean>(false);
  public readonly personCreateError: InputSignal<string | null> = input<string | null>(null);
  public readonly selectedOptionIdChange: OutputEmitterRef<string> = output<string>();
  public readonly addPersonOpenChange: OutputEmitterRef<boolean> = output<boolean>();
  public readonly personNameChange: OutputEmitterRef<string> = output<string>();
  public readonly personRelationChange: OutputEmitterRef<string> = output<string>();
  public readonly personLoginAllowedChange: OutputEmitterRef<boolean> = output<boolean>();
  public readonly createPersonSelected: OutputEmitterRef<void> = output<void>();

  protected readonly choiceOptions: Signal<readonly UiChoiceOption[]> = computed(
    (): readonly UiChoiceOption[] =>
      this.options().map(
        (option: WishlistCreateBeneficiaryOption): UiChoiceOption => ({
          id: option.id,
          label: option.displayName,
          description: `${option.relationLabel} · ${option.description}`,
          iconLabel: option.avatarLabel,
        }),
      ),
  );
  protected readonly selectedOption: Signal<WishlistCreateBeneficiaryOption | null> = computed(
    (): WishlistCreateBeneficiaryOption | null => {
      const selectedOptionId: string | null = this.selectedOptionId();

      return (
        this.options().find(
          (option: WishlistCreateBeneficiaryOption): boolean => option.id === selectedOptionId,
        ) ?? null
      );
    },
  );

  protected handleOptionSelected(optionId: string): void {
    this.selectedOptionIdChange.emit(optionId);
  }

  protected handleAddPersonOpened(): void {
    this.addPersonOpenChange.emit(true);
  }

  protected handleAddPersonClosed(): void {
    this.addPersonOpenChange.emit(false);
  }

  protected handlePersonNameChange(value: string): void {
    this.personNameChange.emit(value);
  }

  protected handlePersonRelationChange(value: string): void {
    this.personRelationChange.emit(value);
  }

  protected handlePersonLoginAllowedChange(value: boolean): void {
    this.personLoginAllowedChange.emit(value);
  }

  protected handleCreatePersonSelected(): void {
    this.createPersonSelected.emit();
  }
}
